import { Table } from '@mantine/core';
import { ICurrency } from '../../../../libraries/currencies';
import { ReadValues } from '../../../../libraries/transactions';
import { Language } from '../../../languages/languageHandler';
import { TransactionDenom } from '../transaction-data';
import { IQuantities, getCurrencyValue } from '../../../utils/script';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

interface Props {
  currency: ICurrency;
  cash?: TransactionDenom[];
  values?: ReadValues[];
}

const prepareQuantitiesUnified = (cash: TransactionDenom[], currency: ICurrency): IQuantities[] => {
  if (!currency) return [];

  return currency.denominations.map(denom => {
    const matchedCash = cash.find(c => c.cid === currency.cid && c.cindex === denom.index);
    const received = matchedCash?.received ?? 0;
    const dispensed = matchedCash?.dispensed ?? 0;
    const qty = received + dispensed;
    const value = denom.value * currency.factor;
    const amount = Number((qty * value).toFixed(2));

    return {
      quantity: qty,
      value,
      amount,
      cindex: denom.index
    };
  });
};

const TransactionTable = ({ currency, cash = [], values = [] }: Props) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);

  const columns: IQuantities[] = prepareQuantitiesUnified(cash, currency);

  const nonZeroColumns = columns.filter(col => col.quantity > 0);
  const hasCash = nonZeroColumns.length > 0;

  return (
    <div>
      {/* --- Denominations Table --- */}
      {hasCash && (
        <Table.ScrollContainer minWidth={500}>
          <Table className="transaction-table" withColumnBorders withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th rowSpan={2}>{currency.currencyStr}</Table.Th>
                <Table.Th colSpan={currency.nCoins}>{lang.GLOBAL_COINS}</Table.Th>
                <Table.Th colSpan={currency.nBills}>{lang.GLOBAL_BILLS}</Table.Th>
              </Table.Tr>
              <Table.Tr>
                {currency.denominations.map(denom => (
                  <Table.Th key={denom.index}>{denom.value * currency.factor}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_QUANTITY}</Table.Th>
                {columns.map((col, index) => (
                  <Table.Td key={index}>{col.quantity}</Table.Td>
                ))}
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
                {columns.map((col, index) => (
                  <Table.Td key={index}>{col.amount.toFixed(2)}</Table.Td>
                ))}
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {/* --- Reading Details Table --- */}
      {values && values.length > 0 && (
        <Table className="bills-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_DENOMINATION}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_SERIAL_NUMBER}</Table.Th>
              <Table.Th>{lang.GLOBAL_ACCEPTED}</Table.Th>
              <Table.Th>{lang.TRANSACTION_SUSPECT}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {values.map((v, i) => (
              <Table.Tr key={i}>
                <Table.Td>{getCurrencyValue(v.cid, v.cindex)}</Table.Td>
                <Table.Td>{v.serial}</Table.Td>
                <Table.Td>
                  {!v.rejected ? (
                    <Icon.IconCheck stroke={3} className="status-ok" />
                  ) : (
                    <Icon.IconX stroke={3} className="status-not-ok" />
                  )}
                </Table.Td>
                <Table.Td>{v.suspicious ? lang.YES : '-'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </div>
  );
};

export default TransactionTable;
